import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  RegressionStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import isArchived from 'archive/isArchived';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {createRenderExpandTableButton} from 'data-frames/toolbar/toolbar-actions/renderExpandTableButton';
import DataFrameTableWrapper, {
  DataFrameTableWrapperOverflowOption,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {withLoadingScreen} from 'util/Promises';

import {
  GetRegressionDataFrames_modelOutputDataFrame_DataFrameNode as GetRegressionOutputDataFrame,
  GetRegressionDataFrames_modelVariablesDataFrame_DataFrameNode as GetRegressionVariablesDataFrame,
} from './regressions/__generated/GetRegressionDataFrames';

enum RegressionDataFramesTab {
  INPUT_DATA_FRAME_TAB = 'INPUT_DATA_FRAME_TAB',
  MODEL_OUTPUT_TAB = 'MODEL_OUTPUT_TAB',
  MODEL_VARIABLES_TAB = 'MODEL_VARIABLES_TAB',
}

const regressionDataFramesTabGlobalState = makeUseGlobalClientState<RegressionDataFramesTab>(
  RegressionDataFramesTab.INPUT_DATA_FRAME_TAB
);

function getOnPromote({
  analysis,
  inputDataFrameId,
  step,
  stepDataName,
}: {
  analysis: PipelineGraphQL;
  inputDataFrameId: string;
  step: GetAnalysisPipelineStep;
  stepDataName: string;
}): () => void {
  return () =>
    withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.PROMOTE_DATASET,
        stepVersion: 0,
        stepConfig: {},
        stepInput: {
          inputDataFrameId,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: step.id,
              stepDataName,
            },
          ],
        },
      })
    );
}

function getAdditionalOverflowOptions({
  analysis,
  inputDataFrameId,
  step,
  stepDataName,
}: {
  analysis: PipelineGraphQL;
  inputDataFrameId: string;
  step: GetAnalysisPipelineStep;
  stepDataName: string;
}): Array<DataFrameTableWrapperOverflowOption> {
  if (isAnalysisReadOnly(analysis)) {
    return [];
  }

  const isDisabled = isArchived(analysis);

  return [
    {
      icon: 'table',
      onSelect: getOnPromote({analysis, inputDataFrameId, step, stepDataName}),
      title: 'Promote to table',
      isDisabled,
      tooltipTitle: isDisabled ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT : undefined,
    },
  ];
}

/** @package */
export interface RegressionDataFramesPreviewProps {
  analysis: PipelineGraphQL;
  clientStateId: string;
  inputDataFrame: VisualizationStepEditorDataFrameNode;
  isExpandedPreviewState: boolean;
  modelOutputDataFrame: GetRegressionOutputDataFrame | null;
  modelVariablesDataFrame: GetRegressionVariablesDataFrame | null;
  regressionStep: GetAnalysisPipelineStep;
  setIsExpandedPreviewState: (newState: boolean) => void;
}

export default function RegressionDataFramesPreview({
  analysis,
  clientStateId,
  inputDataFrame,
  isExpandedPreviewState,
  modelOutputDataFrame,
  modelVariablesDataFrame,
  regressionStep,
  setIsExpandedPreviewState,
}: RegressionDataFramesPreviewProps): JSX.Element {
  const [activeTab, setActiveTab] = regressionDataFramesTabGlobalState.useGlobalClientState(clientStateId);

  const [parentStep] = getParentSteps(analysis, regressionStep);
  const parentStepData = parentStep.outputStepDatas.find((outputStepData) =>
    outputStepData.dataFrames.some((dataFrame) => dataFrame.id === inputDataFrame.id)
  );

  useEffect(() => {
    if (
      activeTab !== RegressionDataFramesTab.INPUT_DATA_FRAME_TAB &&
      (modelOutputDataFrame == null || modelVariablesDataFrame == null)
    ) {
      setActiveTab(RegressionDataFramesTab.INPUT_DATA_FRAME_TAB);
    }
  }, [activeTab, modelOutputDataFrame, modelVariablesDataFrame, setActiveTab]);

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'RegressionDataFramesPreview', element: 'root'})}
    >
      <TabContainer<RegressionDataFramesTab>
        activeTab={
          modelOutputDataFrame == null || modelVariablesDataFrame == null
            ? RegressionDataFramesTab.INPUT_DATA_FRAME_TAB
            : activeTab
        }
        onRequestTabChange={setActiveTab}
        className={css(styles.wrapper)}
        paneClassName={css(styles.wrapper)}
      >
        <Tab tabKey={RegressionDataFramesTab.INPUT_DATA_FRAME_TAB} title="Source table">
          <div className={css(styles.wrapper)}>
            <DataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`${clientStateId}-source-table-preview`}
              dataFrame={inputDataFrame.dataFrameJson}
              dataFrameId={inputDataFrame.id}
              renderIsExcludedColumn={false}
              additionalElements={[
                createRenderExpandTableButton(isExpandedPreviewState, setIsExpandedPreviewState),
              ]}
              additionalOverflowOptions={
                parentStepData == null
                  ? []
                  : getAdditionalOverflowOptions({
                      analysis,
                      inputDataFrameId: inputDataFrame.id,
                      step: parentStep,
                      stepDataName: parentStepData.name,
                    })
              }
            />
          </div>
        </Tab>
        {modelOutputDataFrame != null && (
          <Tab tabKey={RegressionDataFramesTab.MODEL_OUTPUT_TAB} title="Model output table">
            <div className={css(styles.wrapper)}>
              <DataFrameTableWrapper
                actionOrigin={ActionOrigin.ANALYSIS_TOOL}
                clientStateId={`${clientStateId}-output-table-preview`}
                dataFrame={modelOutputDataFrame.dataFrameJson}
                dataFrameId={modelOutputDataFrame.id}
                renderIsExcludedColumn={false}
                additionalElements={[
                  createRenderExpandTableButton(isExpandedPreviewState, setIsExpandedPreviewState),
                ]}
                additionalOverflowOptions={getAdditionalOverflowOptions({
                  analysis,
                  inputDataFrameId: modelOutputDataFrame.id,
                  step: regressionStep,
                  stepDataName: RegressionStepDataName.MODEL_OUTPUT,
                })}
              />
            </div>
          </Tab>
        )}
        {modelVariablesDataFrame != null && (
          <Tab tabKey={RegressionDataFramesTab.MODEL_VARIABLES_TAB} title="Model variables">
            <div className={css(styles.wrapper)}>
              <DataFrameTableWrapper
                actionOrigin={ActionOrigin.ANALYSIS_TOOL}
                clientStateId={`${clientStateId}-variables-table-preview`}
                dataFrame={modelVariablesDataFrame.dataFrameJson}
                dataFrameId={modelVariablesDataFrame.id}
                renderIsExcludedColumn={false}
                additionalElements={[
                  createRenderExpandTableButton(isExpandedPreviewState, setIsExpandedPreviewState),
                ]}
                additionalOverflowOptions={getAdditionalOverflowOptions({
                  analysis,
                  inputDataFrameId: modelVariablesDataFrame.id,
                  step: regressionStep,
                  stepDataName: RegressionStepDataName.MODEL_VARIABLES,
                })}
              />
            </div>
          </Tab>
        )}
      </TabContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
});
